import { css, html, LitElement, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import PGDElement, { Platforms } from "./pg.element";
import { w2grid } from "w2ui";
import style from "../lib/w2ui/w2ui-2.0.pg.css";

interface IColumnProps {
  field?: string;
  text?: string;
  size?: string;
  type?: string;
  render?: any;
  style?: string;
  sortable?: boolean;
}

interface IStatusOptionsProps {
  label?: string;
  bgColor?: string;
  textColor?: string;
}

interface IStatusOptionsBooleanProps {
  true?: IStatusOptionsProps;
  false?: IStatusOptionsProps;
}

const defaultColumns: IColumnProps[] = [
  { field: "name", text: "Name", size: "20%", type: "text", sortable: true },
  { field: "title", text: "Title", size: "20%", type: "text", sortable: true },
  { field: "date", text: "Date", size: "20%", type: "date", sortable: true },
  { field: "number", text: "Number", size: "20%", type: "int", sortable: true },
  { field: "status", text: "Status", size: "20%", type: "div", sortable: true },
];

const defaultRecords = [
  { recid: 1, name: "item A1", title: "item B1", date: "DD/MM/YYYY", number: "0", status: "success" },
  { recid: 2, name: "item A2", title: "item B2", date: "DD/MM/YYYY", number: "0", status: "danger" },
  { recid: 3, name: "item A3", title: "item B3", date: "DD/MM/YYYY", number: "0", status: "warning" },
  { recid: 4, name: "item A4", title: "item B4", date: "DD/MM/YYYY", number: "0", status: "processing" },
  { recid: 5, name: "item A5", title: "item B5", date: "DD/MM/YYYY", number: "0", status: "info" },
  { recid: 6, name: "item A6", title: "item B6", date: "DD/MM/YYYY", number: "0", status: "primary" },
  { recid: 7, name: "item A7", title: "item B7", date: "DD/MM/YYYY", number: "0", status: "none" },
];

const defaultOptions = [
  { label: "success", bgColor: "#F0FFFA", textColor: "#13AF77" },
  { label: "danger", bgColor: "#FFF3F3", textColor: "#FF4D49" },
  { label: "warning", bgColor: "#FFF8EC", textColor: "#FDB528" },
  { label: "processing", bgColor: "#FFFBE4", textColor: "#FFCA0F" },
  { label: "info", bgColor: "#F2FBFF", textColor: "#26C6F9" },
  { label: "primary", bgColor: "#EEEFFF", textColor: "#666CFF" },
  { label: "none", bgColor: "#F6F6F6", textColor: "#6D788D" },
];

const defaultOptionsBoolean = {
  true: { label: "true", bgColor: "#F0FFFA", textColor: "#13AF77" },
  false: { label: "false", bgColor: "#FFF3F3", textColor: "#FF4D49" },
};

interface RecordObject extends Record<string, any> {
  recid: number;
}

@customElement("pg-data-grid")
export class PGDDataGrid extends LitElement implements PGDElement {
  @property({ type: Number }) height = 400;
  @property({ type: String }) headerName = "";
  @property({ type: Boolean }) header = false;
  @property({ type: Boolean }) footer = false;
  @property({ type: Boolean }) toolbar = false;
  @property({ type: Boolean }) lineNumbers = false;
  @property({ type: Boolean }) selectColumn = false;
  @property({ type: Array }) columns?: IColumnProps[];
  @property({ type: Array }) options?: IStatusOptionsProps[];
  @property({ type: Object }) optionsBoolean?: IStatusOptionsBooleanProps;
  @property({ type: Array, reflect: true, attribute: "records" }) records?: RecordObject[];
  @property({ type: Object }) requestData?: {};
  @property({ type: String }) flowKey = "";
  @property({ type: String }) microflow_url = "";

  private grid: any;
  private dataGridColumns: IColumnProps[] = defaultColumns;

  static styles = [
    css`
      ${unsafeCSS(style)}
      :host([w2ui]) {
        display: block;
      }
      #data-grid {
        width: 100%;
      }

      .w2ui-grid {
        border-color: var(--data-grid-bd);
      }

      .w2ui-grid .w2ui-grid-body table .w2ui-head {
        border-color: var(--data-grid-bd);
        background-color: var(--data-grid-header-bg);
        color: var(--data-grid-header-font-color);
      }

      .w2ui-grid .w2ui-grid-body table td {
        border-color: var(--data-grid-bd);
      }

      .w2ui-grid .w2ui-grid-body .w2ui-grid-records table tr.w2ui-odd {
        background-color: var(--data-grid-odd-bg);
        border-color: var(--data-grid-bd);
        color: var(--data-grid-font-color);

        &:hover {
          color: var(--data-grid-font-color);
          background-color: var(--data-grid-odd-bg);
          filter: brightness(0.9);
        }
      }

      .w2ui-grid .w2ui-grid-body .w2ui-grid-records table tr.w2ui-odd.w2ui-empty-record:hover {
        background-color: var(--data-grid-odd-bg);
        filter: brightness(0.9);
      }

      .w2ui-grid .w2ui-grid-body .w2ui-grid-records table tr.w2ui-even {
        border-bottom: 1px solid;
        background-color: var(--data-grid-even-bg);
        border-color: var(--data-grid-bd);
        color: var(--data-grid-font-color);

        &:hover {
          color: var(--data-grid-font-color);
          background-color: var(--data-grid-even-bg);
          filter: brightness(0.9);
        }
      }

      .w2ui-grid .w2ui-grid-body .w2ui-grid-records table tr.w2ui-even.w2ui-empty-record:hover {
        background-color: var(--data-grid-even-bg);
        filter: brightness(0.9);
      }
    `,
  ];

  platform(): string[] {
    return ["web", "mobile"];
  }

  isSupport(platform: Platforms): boolean {
    return this.platform().indexOf(platform) > -1;
  }

  async firstUpdated() {
    this.grid?.destroy();
    await this.onMicroFlowConnect();
    this.generateDataGrid();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    super.updated(changedProperties);
    if (changedProperties.has("microflow_url") && changedProperties.has("requestData")) {
      this.onMicroFlowConnect();
    }

    this.grid?.destroy();
    this.generateDataGrid();
  }

  async getMicroFlowData() {
    const url = this.microflow_url;
    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify(this.requestData),
    });
    return response.json();
  }

  getData = (key: string, data: any) => {
    return key.split(".").reduce((acc, curr) => {
      return acc[curr];
    }, data);
  };

  async onMicroFlowConnect() {
    if (!this.requestData) {
      return;
    }
    const response = await this.getMicroFlowData();
    const flow = this.flowKey.split(".").slice(1).join(".");
    const dataTemp = this.getData(flow, response.responseObjectsMap);
    if (dataTemp?.length === undefined) {
      throw new Error("Invalid microflow data");
    }

    this.dataGridColumns = this.mapMicroflowDataDataGridColumn(dataTemp);
    this.records = dataTemp.map((data: any, index: number) => {
      const record: any = { recid: index + 1 };
      this.columns?.forEach((column: any) => {
        const fieldValue = this.getData(column.field, data);
        record[column.field] = fieldValue;
      });
      return record;
    });
  }

  mapMicroflowDataDataGridColumn(data: Record<string, any>): IColumnProps[] {
    return Object.entries(data).map(([key]) => {
      return {
        field: key,
        text: key,
        size: "20%",
        sortable: true,
      };
    });
  }

  generateDataGrid() {
    const columns = this.columns ?? this.dataGridColumns ?? [];
    const records = this.records ?? defaultRecords;
    const options = this.options ?? defaultOptions;
    const optionsBoolean = this.optionsBoolean ?? defaultOptionsBoolean;

    columns.forEach((column) => {
      if (column.type === "int") {
        column.render = (record: { [key: string]: number }) => {
          const value = (record[column.field!] / 100).toFixed(2);
          return `<p style="text-align: right; margin: 0;">${value}</p>`;
        };
      } else if (column.type === "div") {
        column.render = (record: any) => {
          const isOption = options.find((option) => option.label === record[column.field!]);
          const bgColor = isOption ? isOption.bgColor : "";
          const textColor = isOption ? isOption.textColor : "";
          const label = isOption ? isOption.label : "";

          return `
              <div
                style="width: 73px; height: 19px; border-radius: 30px; background-color: ${bgColor}; display: flex; justify-content: center; align-items: center;"
              >
                <p style="margin: 0; color: ${textColor}; font-weight: 400;">${label}</p>
              </div>
          `;
        };
      } else if (column.type === "toggle") {
        column.render = (record: { [key: string]: any }) => {
          const option = optionsBoolean[record[column.field!] as keyof IStatusOptionsBooleanProps];
          const bgColor = option ? option.bgColor : "";
          const textColor = option ? option.textColor : "";
          const label = option ? option.label : "";

          return `
              <div
                style="width: 73px; height: 19px; border-radius: 30px; background-color: ${bgColor}; display: flex; justify-content: center; align-items: center;"
              >
                <p style="margin: 0; color: ${textColor}; font-weight: 400;">${label}</p>
              </div>
          `;
        };
      }
    });

    // Create the w2ui grid
    this.grid = new w2grid({
      name: "grid",
      box: this.shadowRoot?.querySelector("#data-grid"),
      header: this.headerName,
      show: {
        header: this.header,
        footer: this.footer,
        toolbar: this.toolbar,
        lineNumbers: this.lineNumbers,
        selectColumn: this.selectColumn,
      },
      sortData: columns.map((column) => {
        return {
          field: column.field,
          direction: "asc",
        };
      }),
      columns: columns,
      records: records,
      searches: columns.map((column) => {
        return {
          type: "text",
          field: column.field,
          label: column.text,
        };
      }),
    });

    this.grid.render();
  }

  render() {
    return html`
      <div id="data-grid" style="height: ${this.height}px;">
        <slot></slot>
      </div>
    `;
  }
}
